import { mat4, ReadonlyMat4, ReadonlyVec2, ReadonlyVec3 } from 'gl-matrix';
import { RegistrationUndoService } from '~/domain/undo/RegistrationUndoService';
import { Environment } from '~/engine/data/Environment';
import { BearingDrag } from '~/engine/tool/BearingDrag';
import { buildRotationAroundPivot } from '~/engine/tool/actions/transform-actions/transformUtils';
import { TextureView } from '~/engine/view/TextureView';
import { PlaneView } from '~/engine/view/views/PlaneView';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';
import { ModifierKeys } from '../../ModifierKeys';
import { ToolAction } from '../../ToolAction';

/** Rotation step size for keyboard micro-adjustments in radians (~0.5 degrees) */
const ROTATION_STEP_RAD = 0.5 * (Math.PI / 180);

/**
 * `secondaryToPrimary` followed by a turn of `angleRad` about the line through
 * `pivotMm` along `axis`, both in the primary image's patient space. The
 * positive sense is the right-hand rule about `axis`.
 */
function turnedAboutPrimaryPoint(
    secondaryToPrimary: ReadonlyMat4,
    pivotMm: ReadonlyVec3,
    axis: ReadonlyVec3,
    angleRad: number
): mat4 {
    return mat4.multiply(
        mat4.create(),
        buildRotationAroundPivot(pivotMm, axis, angleRad),
        secondaryToPrimary
    );
}

/**
 * Turns the fused image in the plane of the view, about the crosshair: the
 * point the user can see and place, which stays fixed on screen. A drag turns
 * the image by the change in the cursor's bearing about that point, so the
 * image follows the cursor wherever on the view the drag starts.
 */
export class RotationAction extends ToolAction {
    private readonly mouseButton: number;

    private _startMatrix?: mat4;
    private _drag?: BearingDrag;
    private _registrationId?: string;
    private _didRotate = false;
    private readonly _undoService: RegistrationUndoService;

    public get isActive(): boolean {
        return this._startMatrix !== undefined;
    }

    constructor(mouseButton: number, undoService: RegistrationUndoService) {
        super();
        this.mouseButton = mouseButton;
        this._undoService = undoService;
    }

    public onMousePress(
        button: number,
        _modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: TextureView,
        env?: Environment
    ): EventHandlerResult {
        if (
            button !== this.mouseButton ||
            !(view instanceof PlaneView) ||
            !env?.secondaryImage ||
            !env?.registration ||
            env.registration.isApproved
        ) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        const drag = BearingDrag.start(view, coordinates);
        if (!drag) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        this._drag = drag;
        this._startMatrix = mat4.clone(env.registration.transformation);
        this._registrationId = env.registration.sopInstanceUid;
        this._didRotate = false;

        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            true
        );
    }

    public onMouseMove(
        coordinates: ReadonlyVec2,
        _buttons: number,
        _view: TextureView,
        env?: Environment
    ): EventHandlerResult {
        if (!this._startMatrix || !this._drag || !env?.registration) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        // A positive bearing is clockwise on screen, which is a negative turn
        // about the view direction.
        const newMatrix = turnedAboutPrimaryPoint(
            this._startMatrix,
            this._drag.pivotMm,
            this._drag.viewNormal,
            -this._drag.angleRadTo(coordinates)
        );

        env.setRegistrationMatrix(newMatrix);
        this._didRotate = true;

        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    public onMouseRelease(
        _button?: number,
        _coordinates?: ReadonlyVec2,
        _view?: TextureView,
        env?: Environment
    ): EventHandlerResult {
        if (this._startMatrix && this._registrationId && this._didRotate) {
            this._undoService.pushState(
                this._registrationId,
                this._startMatrix
            );
            env?.registration?.syncTransformationToDataset();
        }
        this.clearDrag();
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    /**
     * Handles Ctrl/Cmd + Arrow key presses for micro-rotation
     * adjustments.
     */
    public onKeyPress(
        key: string,
        modifierKeys: ModifierKeys,
        view: TextureView,
        env?: Environment
    ): EventHandlerResult {
        if (
            !modifierKeys.hasCtrl ||
            !(view instanceof PlaneView) ||
            !env?.secondaryImage ||
            !env?.registration ||
            env.registration.isApproved
        ) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        let angle = 0;
        if (key === 'ArrowLeft') {
            angle = ROTATION_STEP_RAD;
        } else if (key === 'ArrowRight') {
            angle = -ROTATION_STEP_RAD;
        }

        if (angle === 0) {
            // Ctrl/Cmd+Up and Down are the nudge chord, which turns nothing
            // here; consumed, so the chord never scrolls the slice instead.
            return new EventHandlerResult(
                key === 'ArrowUp' || key === 'ArrowDown'
                    ? EventHandlerResultType.EVENT_HANDLED
                    : EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        const previousMatrix = mat4.clone(env.registration.transformation);
        const newMatrix = turnedAboutPrimaryPoint(
            previousMatrix,
            view.planeIntersectionPoint.coordinates,
            view.viewDirection,
            angle
        );

        env.setRegistrationMatrix(newMatrix);

        this._undoService.pushState(
            env.registration.sopInstanceUid,
            previousMatrix
        );
        env.registration.syncTransformationToDataset();

        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    public cancelAction(env?: Environment): void {
        if (this._startMatrix && env?.registration) {
            env.setRegistrationMatrix(mat4.clone(this._startMatrix));
        }
        this.clearDrag();
    }

    private clearDrag(): void {
        this._startMatrix = undefined;
        this._drag = undefined;
        this._registrationId = undefined;
        this._didRotate = false;
    }
}
